import { ReactElement, useState } from 'react';
import cx from 'clsx';
import { useField, useForm } from 'react-final-form';
import { Box, DatePicker } from '@ff-it/ui';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, isSameDay, isSameMonth, format } from 'date-fns';
import { FilterChip } from './FilterChip';
import { dateCalendar, fullPicker } from './dateChip.css';
import { pickerTabs } from './DateRangeFilterChip.css';
import { toDate, toISO } from './dateChip';

const MONTH_FORMAT = 'MMM. yyyy';

type Mode = 'range' | 'month' | 'year';

const isWholeYear = (from: Date, to: Date): boolean =>
  isSameDay(from, startOfYear(from)) && isSameDay(to, endOfYear(from));

const isSingleMonth = (from: Date, to: Date): boolean =>
  isSameMonth(from, to) && isSameDay(from, startOfMonth(from)) && isSameDay(to, endOfMonth(to));

const deriveMode = (from: Date | null, to: Date | null): Mode => {
  if (from && to) {
    if (isWholeYear(from, to)) {
      return 'year';
    }
    if (isSingleMonth(from, to)) {
      return 'month';
    }
  }
  return 'range';
};

function PeriodPicker({
  from,
  to,
  minDate,
  maxDate,
  onApply,
  close,
}: {
  from: Date | null;
  to: Date | null;
  minDate?: Date;
  maxDate?: Date;
  onApply: (a: Date, b: Date) => void;
  close: () => void;
}): ReactElement {
  // opens on the tab matching the current value
  const [mode, setMode] = useState<Mode>(() => deriveMode(from, to));
  // in-progress range for selectsRange: the grid is driven by this draft so the first-click start shows while the user
  // picks the end
  const [draft, setDraft] = useState<[Date | null, Date | null]>([from, to]);

  const commitRange = (range: [Date | null, Date | null]): void => {
    setDraft(range);
    // apply once both ends are chosen; snap to whole-month bounds and order them
    const [r0, r1] = range;
    if (r0 && r1) {
      const [a, b] = r0 <= r1 ? [r0, r1] : [r1, r0];
      onApply(startOfMonth(a), endOfMonth(b));
      close();
    }
  };

  const tab = (m: Mode, title: string): ReactElement => (
    <li className="nav-item">
      <button type="button" className={cx('nav-link', mode === m && 'active')} onClick={() => setMode(m)}>
        {title}
      </button>
    </li>
  );

  return (
    <Box>
      <ul className={cx('nav nav-tabs px-2 pt-2', pickerTabs)}>
        {tab('range', 'Range')}
        {tab('month', 'Month')}
        {tab('year', 'Year')}
      </ul>
      <Box className={cx(dateCalendar, fullPicker)}>
        {mode === 'range' && (
          <DatePicker
            selectsRange
            inline
            showMonthYearPicker
            startDate={draft[0]}
            endDate={draft[1]}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(range: [Date | null, Date | null]) => commitRange(range)}
          />
        )}
        {mode === 'month' && (
          <DatePicker
            showMonthYearPicker
            inline
            selected={from}
            minDate={minDate}
            maxDate={maxDate}
            allowSameDay
            onChange={(d: Date | null) => {
              if (d) {
                onApply(startOfMonth(d), endOfMonth(d));
                close();
              }
            }}
          />
        )}
        {mode === 'year' && (
          <DatePicker
            showYearPicker
            inline
            selected={from}
            minDate={minDate}
            maxDate={maxDate}
            allowSameDay
            onChange={(d: Date | null) => {
              if (d) {
                onApply(startOfYear(d), endOfYear(d));
                close();
              }
            }}
          />
        )}
      </Box>
    </Box>
  );
}

type MonthRangeFilterChipProps = {
  label: string;
  fromName: string;
  toName: string;
  minDate?: Date;
  maxDate?: Date;
  defaultFrom?: string;
  defaultTo?: string;
};

export function MonthRangeFilterChip({
  label,
  fromName,
  toName,
  minDate,
  maxDate,
  defaultFrom,
  defaultTo,
}: MonthRangeFilterChipProps): ReactElement {
  const form = useForm();
  const {
    input: { value: fromValue },
  } = useField<string | undefined>(fromName, { subscription: { value: true } });
  const {
    input: { value: toValue },
  } = useField<string | undefined>(toName, { subscription: { value: true } });
  const from = toDate(fromValue);
  const to = toDate(toValue);
  const hasValue = !!(fromValue && toValue);
  const atDefault = defaultFrom != null && defaultTo != null && fromValue === defaultFrom && toValue === defaultTo;

  const apply = (a: Date, b: Date): void => {
    form.change(fromName, toISO(a));
    form.change(toName, toISO(b));
  };

  const valueNode =
    hasValue && from && to
      ? isWholeYear(from, to)
        ? format(from, 'yyyy')
        : isSameMonth(from, to)
          ? format(from, MONTH_FORMAT)
          : `${format(from, MONTH_FORMAT)} – ${format(to, MONTH_FORMAT)}`
      : null;

  return (
    <FilterChip
      label={label}
      active={hasValue && !atDefault}
      valueNode={valueNode}
      onClear={() => {
        form.change(fromName, defaultFrom ?? undefined);
        form.change(toName, defaultTo ?? undefined);
      }}
      width={null}
      flush
    >
      {(close) => (
        <PeriodPicker from={from} to={to} minDate={minDate} maxDate={maxDate} onApply={apply} close={close} />
      )}
    </FilterChip>
  );
}
